import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, Sparkles } from "lucide-react";
import type React from "react";
import { Link } from "react-router-dom";

const fadeUp = {
	initial: { opacity: 0, y: 18 },
	whileInView: { opacity: 1, y: 0 },
	viewport: { once: true, amount: 0.35 },
	transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
};

const LandingPage: React.FC = () => {
	return (
		<div className="relative min-h-screen overflow-x-hidden surface-gradient text-primary">
			<div className="relative mx-auto flex w-full max-w-6xl flex-col px-6 pb-16 pt-8 md:px-10">
				<header className="mb-16 flex items-center justify-between rounded-2xl border border-subtle surface-alt px-5 py-3">
					<div className="text-sm font-semibold tracking-[0.18em] text-muted uppercase">KnowGem</div>
					<div className="flex items-center gap-2">
						<Link to="/auth" className="btn btn-secondary btn-sm">
							Sign in
						</Link>
						<Link to="/app" className="btn btn-primary btn-sm">
							Open App
						</Link>
					</div>
				</header>

				<motion.section {...fadeUp} className="grid items-center gap-10 md:grid-cols-[1.05fr_0.95fr]">
					<div>
						<div className="mb-5 inline-flex items-center gap-2 rounded-full border border-subtle surface-alt px-3 py-1 text-xs text-muted">
							<Sparkles className="h-3.5 w-3.5 text-primary-500" />
							Knowledge Graph Workspace
						</div>
						<h1 className="font-['Space_Grotesk','Inter','system-ui','sans-serif'] text-4xl font-semibold leading-tight md:text-6xl">
							Find your knowledge gems.
						</h1>
						<p className="mt-6 max-w-xl text-base leading-relaxed text-muted md:text-lg">
							A focused workspace to connect concepts from documents, visualize relationships, and validate formal logic
							in one place.
						</p>
						<div className="mt-8 flex flex-wrap items-center gap-3">
							<Link to="/auth" className="btn btn-primary btn-md">
								Start
								<ArrowRight className="ml-2 h-4 w-4" />
							</Link>
							<Link to="/graph?mock=true" className="btn btn-secondary btn-md">
								Explore Graph
							</Link>
						</div>
					</div>

					<motion.div
						initial={{ opacity: 0, scale: 0.96 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{ duration: 0.7, ease: "easeOut" }}
						className="rounded-3xl border border-strong surface-alt p-5 shadow-xl"
					>
						<div className="mb-4">
							<div className="text-sm font-semibold text-primary">Product Demo</div>
						</div>
						<motion.div
							initial={{ opacity: 0, y: 8 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.55, delay: 0.1 }}
							className="relative overflow-hidden rounded-2xl border border-subtle surface-alt"
						>
							<div className="aspect-video w-full bg-[linear-gradient(155deg,color-mix(in_srgb,var(--surface)_85%,transparent),color-mix(in_srgb,var(--surface-2)_90%,transparent))]" />
							<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_25%,rgba(255,255,255,0.18),transparent_42%)] dark:bg-[radial-gradient(circle_at_30%_25%,rgba(255,255,255,0.08),transparent_42%)]" />
							<div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
								<PlayCircle className="h-12 w-12 text-primary-500/80" />
								<span className="text-xs font-medium text-muted">Demo Video coming soon</span>
							</div>
							{/* <iframe
							 	width="560" 
								height="315" 
								src="https://www.youtube.com/embed/Bm5iA4Zupek?si=O2KJRdlv94VnmYjY" 
								title="YouTube video player" 
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
								referrerPolicy="strict-origin-when-cross-origin" allowFullScreen>	
							</iframe> */}
						</motion.div>
					</motion.div>
				</motion.section>

				<section className="mt-16 grid gap-4 md:grid-cols-3">
					{[
						{
							title: "Structured Context",
							desc: "Unify scattered sources into one graph context that is easy to navigate in real time.",
						},
						{
							title: "Visual Reasoning",
							desc: "Separate node importance, relationship density, and communities to find insights faster.",
						},
						{
							title: "Formal Validation",
							desc: "Validate reasoning outputs with HOL expressions to support evidence-based decisions.",
						},
					].map((card, idx) => (
						<motion.article
							key={card.title}
							initial={{ opacity: 0, y: 16 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.5, delay: idx * 0.08 }}
							className="rounded-2xl border border-subtle surface-alt p-5"
						>
							<h3 className="text-base font-semibold">{card.title}</h3>
							<p className="mt-2 text-sm leading-relaxed text-muted">{card.desc}</p>
						</motion.article>
					))}
				</section>
			</div>
		</div>
	);
};

export default LandingPage;
